/**
 * Admin › Configurações › **Regras governadas**, the Claude Design redesign (`Admin Web - Regras Governadas.dc.html`, screen A12; D762) — the pure
 * view model of the LEVEL model the new screen is built on.
 *
 * The screen no longer asks the operator to pick a "bucket" out of a list. It shows ONE place — Plataforma inteira › País › Estado › Cidade, the
 * place the top scope selector is on — and the operator picks the LEVEL they look at and edit. Every rule in the list then says where its value at
 * that level comes from: its own value ("próprio"), a level above ("herdado") or nothing ("sem valor"). This module computes that, groups the rules by
 * domain and filters them; the server still decides everything (the fold of `GET /v1/config/settings/:key/effective`, the four eyes, every refusal).
 *
 * The fold here is the list's ANSWER AT A GLANCE (most specific level with a version in force, among the levels the key allows); the detail panel
 * reads the server's own fold for the selected rule, so a layer held by a lock above shows why.
 *
 * Neutral: keys, scope refs and tokens are data; no country, currency or locale literal.
 */
import type { GovernedSettingBucketView, GovernedSettingScope, GovernedSettingSummaryView } from '@vello/api-client';
import type { OperatorScope } from './shell.js';
import { countryOfMarket } from './scope.js';

/** One level of a place: the platform (`scopeRef` empty), a country (`BR`), a state (`BR-MG`) or a market (`br:patos-de-minas`). */
export interface RuleLevel {
  readonly scope: Extract<GovernedSettingScope, 'platform' | 'country' | 'region' | 'market'>;
  readonly scopeRef: string;
}

/** The stable id of a level (`platform|`, `country|BR`, `region|BR-MG`, `market|br:patos-de-minas`). */
export const levelId = (level: { readonly scope: string; readonly scopeRef: string }): string => `${level.scope}|${level.scopeRef}`;

/** Whether a bucket (or a change, or a chain layer) is the given level. Country and state codes compare without case; a market id is lower-case. */
export function isLevel(level: RuleLevel, at: { readonly scope: string; readonly scopeRef: string }): boolean {
  return level.scope === at.scope && level.scopeRef.toUpperCase() === at.scopeRef.toUpperCase();
}

interface MarketLike {
  readonly id: string;
  readonly regionCode?: string;
  readonly timezone?: string;
}

/**
 * The levels of a place, BROADEST FIRST: the platform, then its country, its state and its market when the place has them. A city scope never
 * carries its state (`scope.ts`): the state comes from the market registry. A place with no country (the whole platform) has the platform only.
 */
export function levelsOfPlace(place: OperatorScope, markets: readonly MarketLike[] = []): readonly RuleLevel[] {
  const city = place.city !== undefined && place.city !== '' ? place.city : undefined;
  const region = place.region ?? (city !== undefined ? markets.find((m) => m.id === city)?.regionCode : undefined);
  const country = place.country ?? (region !== undefined ? region.slice(0, 2) : city !== undefined ? countryOfMarket(city) : undefined);
  return [
    { scope: 'platform', scopeRef: '' },
    ...(country !== undefined && country !== '' ? [{ scope: 'country' as const, scopeRef: country.toUpperCase() }] : []),
    ...(region !== undefined && region !== '' ? [{ scope: 'region' as const, scopeRef: region.toUpperCase() }] : []),
    ...(city !== undefined ? [{ scope: 'market' as const, scopeRef: city }] : []),
  ];
}

/** A level written in a link (`platform|`, `country|PT`, `region|BR-MG`, `market|br:patos-de-minas`), or undefined when it is not one. */
export function levelFromQuery(raw: string | undefined): RuleLevel | undefined {
  if (raw === undefined) return undefined;
  const bar = raw.indexOf('|');
  const scope = bar < 0 ? raw : raw.slice(0, bar);
  const ref = bar < 0 ? '' : raw.slice(bar + 1).trim();
  if (scope === 'platform') return { scope, scopeRef: '' };
  if (scope === 'country' && /^[A-Za-z]{2}$/.test(ref)) return { scope, scopeRef: ref.toUpperCase() };
  if (scope === 'region' && /^[A-Za-z]{2}-[A-Za-z0-9]{1,3}$/.test(ref)) return { scope, scopeRef: ref.toUpperCase() };
  if (scope === 'market' && /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/.test(ref)) return { scope, scopeRef: ref };
  return undefined;
}

/** The link value of a level (the inverse of {@link levelFromQuery}). */
export const levelQuery = (level: RuleLevel): string => levelId(level);

/** The place a bucket stands for — what the screen moves to when the operator opens a value held somewhere else. */
export function placeOfLevel(level: { readonly scope: string; readonly scopeRef: string }): OperatorScope {
  if (level.scope === 'country') return { country: level.scopeRef.toUpperCase() };
  if (level.scope === 'region') return { country: level.scopeRef.slice(0, 2).toUpperCase(), region: level.scopeRef.toUpperCase() };
  if (level.scope === 'market') return { country: countryOfMarket(level.scopeRef), city: level.scopeRef };
  return {};
}

/** Where the value of a rule at a level comes from. */
export type RuleSource =
  | { readonly kind: 'own'; readonly level: RuleLevel }
  | { readonly kind: 'inherited'; readonly level: RuleLevel }
  | { readonly kind: 'none' };

const holds = (bucket: GovernedSettingBucketView | undefined): boolean => bucket?.current !== undefined;

/** The bucket a key holds at a level, if any. */
export function bucketAt(buckets: readonly GovernedSettingBucketView[], level: RuleLevel): GovernedSettingBucketView | undefined {
  return buckets.find((b) => isLevel(level, b));
}

/**
 * The fold the list shows: from the selected level up to the platform, the first level the key ALLOWS that holds a version in force. `levels` are
 * broadest first and `selected` is an index into them.
 */
export function ruleSource(rule: Pick<GovernedSettingSummaryView, 'allowedScopes' | 'buckets'>, levels: readonly RuleLevel[], selected: number): RuleSource {
  for (let i = Math.min(selected, levels.length - 1); i >= 0; i--) {
    const level = levels[i] as RuleLevel;
    if (!rule.allowedScopes.includes(level.scope)) continue;
    if (holds(bucketAt(rule.buckets, level))) return { kind: i === selected ? 'own' : 'inherited', level };
  }
  return { kind: 'none' };
}

/** What waits for a person on a rule: the proposals in review plus the applied rollbacks waiting for their post-review. */
export const rulePendingCount = (rule: Pick<GovernedSettingSummaryView, 'pendingCount' | 'awaitingReviewCount'>): number => rule.pendingCount + (rule.awaitingReviewCount ?? 0);

// ---- domains -------------------------------------------------------------------------------------

/** The domains the list is grouped by, in the design's order (the last two complete it: a catalogued key of no known area, and a key with no name yet). */
export const RULE_DOMAINS = ['dispatch', 'payment', 'desk', 'security', 'maps', 'commerce', 'accounts', 'ai', 'other', 'uncatalogued'] as const;
export type RuleDomain = (typeof RULE_DOMAINS)[number];

/** Keys whose area is not the one their prefix says. */
const DOMAIN_OF_KEY: Readonly<Record<string, RuleDomain>> = {
  'ride.tip': 'payment',
  'partner.deskRideCategories': 'desk',
  'driver.dutyVehicle': 'dispatch',
  'driver.vehicleRegistration': 'accounts',
  'rider.nearbyVehicles': 'dispatch',
  'rider.searchRadar': 'dispatch',
  'manual.ask': 'ai',
  'driver.signup': 'accounts',
  'driver.signup.minimumAgeYears': 'accounts',
  'rider.photo': 'accounts',
  'promotion.driverReferral': 'accounts',
  // D877: the session mode is chosen when going online, next to the duty vehicle
  'provider.sessionModes': 'dispatch',
};

const DOMAIN_OF_PREFIX: Readonly<Record<string, RuleDomain>> = {
  dispatch: 'dispatch', ride: 'dispatch',
  payment: 'payment', pricing: 'payment', fare: 'payment', finance: 'payment', calendar: 'payment',
  central: 'desk', support: 'desk', partner: 'desk',
  identity: 'security', phone: 'security', safety: 'security', privacy: 'security', app: 'security',
  geo: 'maps', driver: 'maps', notification: 'maps',
  food: 'commerce', merchant: 'commerce', send: 'commerce',
  rider: 'accounts', promotion: 'accounts',
  // D877: the provider's service lines (enrollment per line) sit with the provider sign-up and vehicle registration
  provider: 'accounts',
  ai: 'ai',
};

/** The area a key belongs to. A key the vocabulary has no name for is `uncatalogued`, whatever its prefix. */
export function ruleDomain(key: string, catalogued: boolean): RuleDomain {
  if (!catalogued) return 'uncatalogued';
  return DOMAIN_OF_KEY[key] ?? DOMAIN_OF_PREFIX[key.split('.')[0] ?? ''] ?? 'other';
}

// ---- search, filter and grouping -----------------------------------------------------------------

export type RuleFilter = 'all' | 'pending' | 'empty';

const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Whether a rule matches the search: its name or its key contains the text, ignoring case and accents. */
export function ruleMatches(query: string, name: string, key: string): boolean {
  const q = fold(query.trim());
  return q === '' || fold(name).includes(q) || fold(key).includes(q);
}

/** One rule as the list renders it. */
export interface RuleRow {
  readonly key: string;
  readonly name: string;
  readonly catalogued: boolean;
  readonly domain: RuleDomain;
  readonly source: RuleSource;
  readonly pending: number;
}

/** The rows of a list, with the counts of each filter (computed over the search, before the filter). */
export interface RuleList {
  readonly groups: readonly { readonly domain: RuleDomain; readonly rows: readonly RuleRow[] }[];
  readonly counts: Readonly<Record<RuleFilter, number>>;
  readonly total: number;
}

/** Group the rows by domain (the design's order; a domain with no row is left out), after the search and the filter. */
export function ruleList(rows: readonly RuleRow[], query: string, filter: RuleFilter): RuleList {
  const searched = rows.filter((r) => ruleMatches(query, r.name, r.key));
  const counts = { all: searched.length, pending: searched.filter((r) => r.pending > 0).length, empty: searched.filter((r) => r.source.kind === 'none').length };
  const kept = searched.filter((r) => (filter === 'pending' ? r.pending > 0 : filter === 'empty' ? r.source.kind === 'none' : true));
  const groups = RULE_DOMAINS.map((domain) => ({ domain, rows: kept.filter((r) => r.domain === domain) })).filter((g) => g.rows.length > 0);
  return { groups, counts, total: kept.length };
}

// ---- the "value per level" chain -----------------------------------------------------------------

/** How a level reads in the chain of the selected rule. */
export type ChainTag = 'appliesHere' | 'appliesInherited' | 'overridden' | 'ownValue' | 'inherits' | 'notSet' | 'notAllowed';

export interface ChainRow {
  readonly level: RuleLevel;
  readonly bucket?: GovernedSettingBucketView;
  /** The level the operator is looking at. */
  readonly here: boolean;
  /** More specific than the selected level (it does not decide here; it is shown greyed). */
  readonly below: boolean;
  /** This level's version is the one in force at the selected level. */
  readonly decides: boolean;
  readonly tag: ChainTag;
}

/**
 * The chain of the selected rule, MOST SPECIFIC FIRST (Cidade, Estado, País, Plataforma): what each level holds and which one decides at the selected
 * level. `winner` is the server's fold for the selected level when it answered (it knows about locks); without it the list's own fold decides.
 */
export function chainRows(
  rule: Pick<GovernedSettingSummaryView, 'allowedScopes'> & { readonly buckets: readonly GovernedSettingBucketView[] },
  levels: readonly RuleLevel[],
  selected: number,
  winner?: { readonly scope?: string; readonly scopeRef?: string },
): readonly ChainRow[] {
  const local = ruleSource(rule, levels, selected);
  const decider: { scope: string; scopeRef: string } | undefined = winner !== undefined
    ? (winner.scope !== undefined ? { scope: winner.scope, scopeRef: winner.scopeRef ?? '' } : undefined)
    : (local.kind === 'none' ? undefined : local.level);
  return levels.map((level, i) => {
    const bucket = bucketAt(rule.buckets, level);
    const allowed = rule.allowedScopes.includes(level.scope);
    const decides = decider !== undefined && isLevel(level, decider);
    const below = i > selected;
    const own = holds(bucket);
    const tag: ChainTag = !allowed ? 'notAllowed'
      : decides ? (i === selected ? 'appliesHere' : 'appliesInherited')
        : own ? (below ? 'ownValue' : 'overridden')
          : below ? 'inherits' : 'notSet';
    return { level, ...(bucket !== undefined ? { bucket } : {}), here: i === selected, below, decides, tag };
  }).reverse();
}

/** The places other than the levels on screen where a rule holds a value of its own (each one can be opened). */
export function otherPlaces(buckets: readonly GovernedSettingBucketView[], levels: readonly RuleLevel[]): readonly RuleLevel[] {
  const rank = (s: string): number => (s === 'platform' ? 0 : s === 'country' ? 1 : s === 'region' ? 2 : 3);
  return buckets
    .filter((b) => holds(b) && (b.scope === 'platform' || b.scope === 'country' || b.scope === 'region' || b.scope === 'market') && !levels.some((l) => isLevel(l, b)))
    .map((b) => ({ scope: b.scope as RuleLevel['scope'], scopeRef: b.scopeRef }))
    .sort((a, b) => rank(a.scope) - rank(b.scope) || a.scopeRef.localeCompare(b.scopeRef));
}

/**
 * The bucket the editor proposes into at a level: the one the server holds, or an EMPTY one (a first value is proposed from here). An empty market
 * bucket takes the market's time zone from the registry, so a change can still be scheduled in the market's wall clock.
 */
export function bucketForLevel(buckets: readonly GovernedSettingBucketView[], level: RuleLevel, defaultValue: unknown, markets: readonly MarketLike[] = []): GovernedSettingBucketView {
  const held = bucketAt(buckets, level);
  if (held !== undefined) return held;
  const timezone = level.scope === 'market' ? markets.find((m) => m.id === level.scopeRef)?.timezone : undefined;
  return {
    scope: level.scope,
    scopeRef: level.scopeRef,
    ...(timezone !== undefined ? { timezone } : {}),
    latestVersion: 0,
    base: defaultValue !== undefined && level.scope !== 'platform' ? { source: 'DEFAULT', value: defaultValue } : { source: 'DEFAULT' },
    scheduledVersions: [],
    pending: [],
  };
}

/** Why "Enviar para revisão" is off, in the order the operator meets it (or `ready`). */
export type SubmitBlock = 'notAllowed' | 'noValue' | 'unchanged' | 'noReason' | 'schedule' | 'collision' | 'ready';

export function submitBlock(input: {
  readonly allowed: boolean;
  readonly hasDraft: boolean;
  readonly changes: boolean;
  readonly reason: string;
  readonly scheduleIssue: boolean;
  readonly collisionUnacknowledged: boolean;
}): SubmitBlock {
  if (!input.allowed) return 'notAllowed';
  if (!input.hasDraft) return 'noValue';
  if (!input.changes) return 'unchanged';
  if (input.reason.trim() === '') return 'noReason';
  if (input.scheduleIssue) return 'schedule';
  if (input.collisionUnacknowledged) return 'collision';
  return 'ready';
}
